import { queryOptions } from '@tanstack/react-query';

import { parseSchema } from '@repo/utils/parse-schema';

import { http } from '~/services/http.ts';

import type {
  CreateBasisRequest,
  UpdateBasisRequest,
  UpdateSchemaItemLimitsRequest,
} from './types.ts';
import {
  projectCompositionBasisSchema,
  projectCompositionGeneralNoteSchema,
  projectCompositionSchemaItemSchema,
  projectCompositionSchemaSchema,
} from './types.ts';

const cargoApiBase = `${import.meta.env.VITE_API_BASE}/cargo`;

function projectCompositionBase(projectId: string) {
  return `${cargoApiBase}/projects/${projectId}/composition`;
}

function parseNullableResponse<T>(schema: { parse: (data: unknown) => T }, data: unknown): T | null {
  if (data === undefined || data === null)
    return null;
  return schema.parse(data);
}

export async function getProjectBases(projectId: string) {
  return parseSchema(
    projectCompositionBasisSchema.array(),
    await http(`${projectCompositionBase(projectId)}/bases`),
  );
}

export function queryProjectBases(projectId: string) {
  return queryOptions({
    queryKey: ['composition', 'projects', projectId, 'bases'],
    queryFn: () => getProjectBases(projectId),
  });
}

export async function createBasis(projectId: string, body: CreateBasisRequest) {
  return parseSchema(
    projectCompositionBasisSchema,
    await http(`${projectCompositionBase(projectId)}/bases`, { method: 'POST', body }),
  );
}

export async function updateBasis(projectId: string, basisId: string, body: UpdateBasisRequest) {
  return parseSchema(
    projectCompositionBasisSchema,
    await http(`${projectCompositionBase(projectId)}/bases/${basisId}`, { method: 'PUT', body }),
  );
}

export async function supersedeBasis(projectId: string, basisId: string) {
  return parseSchema(
    projectCompositionBasisSchema,
    await http(`${projectCompositionBase(projectId)}/bases/${basisId}/supersede`, { method: 'POST' }),
  );
}

export async function deleteBasis(projectId: string, basisId: string) {
  await http(`${projectCompositionBase(projectId)}/bases/${basisId}`, { method: 'DELETE' });
}

export async function getProjectGeneralNote(projectId: string) {
  return parseNullableResponse(
    projectCompositionGeneralNoteSchema.nullable(),
    await http(`${projectCompositionBase(projectId)}/general-note`),
  );
}

export function queryProjectGeneralNote(projectId: string) {
  return queryOptions({
    queryKey: ['composition', 'projects', projectId, 'general-note'],
    queryFn: () => getProjectGeneralNote(projectId),
  });
}

export async function updateProjectGeneralNote(projectId: string, note: string | null) {
  return parseSchema(
    projectCompositionGeneralNoteSchema,
    await http(`${projectCompositionBase(projectId)}/general-note`, {
      method: 'PUT',
      body: { note },
    }),
  );
}

export async function getProjectSchema(projectId: string) {
  return parseNullableResponse(
    projectCompositionSchemaSchema.nullable(),
    await http(`${projectCompositionBase(projectId)}/schema`),
  );
}

export function queryProjectSchema(projectId: string) {
  return queryOptions({
    queryKey: ['composition', 'projects', projectId, 'schema'],
    queryFn: () => getProjectSchema(projectId),
  });
}

export async function updateSchemaItemLimits(
  projectId: string,
  componentKey: string,
  body: UpdateSchemaItemLimitsRequest,
) {
  return parseSchema(
    projectCompositionSchemaItemSchema,
    await http(`${projectCompositionBase(projectId)}/schema/items/${componentKey}/limits`, {
      method: 'PUT',
      body,
    }),
  );
}
